import { abrirSeletorDeData } from '../utils/abrirSeletorDeData';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Reminder } from '../types/Reminder';
import { PaymentMethod } from '../types/PaymentMethod';
import { Transaction, TransactionType } from '../types/Transaction';
import {
  getReminders,
  createReminder,
  updateReminder,
  deleteReminder,
  checkReminder,
  uncheckReminder,
} from '../services/api';
import './Reminders.css';
import EntityQuickEntry from './EntityQuickEntry';
import EdicaoModal from './EdicaoModal';
import { mensagemDeErro } from '../utils/mensagemDeErro';
import Toast from './Toast';
/* O MESMO número que o schema do servidor usa — ver utils/limites.ts. */
import { LIMITES } from '../utils/limites';
import { brlOrMasked } from '../utils/format';
import { useConfirmar } from '../contexts/Confirmacao';
import { useFechavelComVoltar } from '../contexts/VoltarDoCelular';

interface RemindersProps {
  /** O olhinho de "Ocultar valores" do cabeçalho. Ver `utils/format.ts`. */
  hideValues?: boolean;

  activeMonth: { key: string; label: string };
  /** Vem do App, que já as carregou. Só para o seletor de ligação. */
  transactions: Transaction[];
  /** Os métodos, para o seletor "fatura de qual cartão" (15/09). */
  paymentMethods?: PaymentMethod[];
  /**
   * Sobe quando algo fora daqui pode ter marcado um lembrete (15/09): dar a
   * fatura como paga, na lista ou em Métodos, marca o lembrete ligado.
   */
  refreshTrigger?: number;
  /**
   * Avisa que algo mudou aqui: criou, editou, apagou, marcou ou desmarcou.
   *
   * A lista de Transações mostra ⏳ ou ✓ a partir DESTES lembretes, e o saldo
   * soma o que ainda não se moveu. Sem este aviso, marcar um lembrete como
   * feito não mexia em nada na outra seção até um F5 — o estado certo no
   * banco e a tela velha na frente dela.
   */
  onMudou?: () => void;
}

interface FormState {
  description: string;
  type: 'expense' | 'income';
  value: string;
  dayOfMonth: string;
  /** Vazio = sem prazo, que é o comportamento de sempre. */
  endDate: string;
  /** Vazio = solto, sem transação ligada. */
  transactionId: string;
  /** Vazio = não é lembrete de fatura. */
  paymentMethodId: string;
}

const EMPTY_FORM: FormState = {
  description: '',
  type: 'expense',
  value: '',
  dayOfMonth: '1',
  endDate: '',
  transactionId: '',
  paymentMethodId: '',
};

const Reminders: React.FC<RemindersProps> = ({ activeMonth, transactions, paymentMethods = [], refreshTrigger, onMudou, hideValues }) => {
  /* Só cartão com dia de pagamento tem fatura para marcar. */
  const cartoesComFatura = paymentMethods.filter(m => m.billingDay);
  const confirmar = useConfirmar();
  /**
   * O que dá para ligar a um lembrete.
   *
   * DESPESA E RECEITA. A primeira versão só listava despesa, e estava errada:
   * o saldo já trata receita pendente (ela SAI, porque o dinheiro ainda não
   * chegou), então impedir de ligar uma receita deixava esse tratamento sem
   * como acontecer — código vivo do lado do cálculo, impossível do lado da
   * tela.
   *
   * O que fica de fora é transferência de reserva: ela não é conta a pagar
   * nem dinheiro a receber, é dinheiro mudando de lugar dentro da casa.
   *
   * Ordenado por data mais recente primeiro: o que ela quer ligar quase
   * sempre é algo que acabou de lançar, e uma lista alfabética esconde isso
   * no meio de tudo.
   */
  const transacoesLigaveis = useMemo(
    () => transactions
      .filter(t => !t.debitDirection)
      .slice()
      .sort((a, b) => String(b.date).localeCompare(String(a.date))),
    [transactions],
  );

  /* Delega para `brlOrMasked`: é o que faz o olhinho de "Ocultar valores"
     alcançar esta seção. Antes era um `Intl.NumberFormat` próprio, e como não
     havia um lugar por onde todo dinheiro passasse, o olhinho só chegava nos
     quatro cartões do topo. */
  const fmtBRL = (v: number) => brlOrMasked(v, !!hideValues);

  const [reminders, setReminders] = useState<Reminder[]>([]);
  /* O erro do servidor vai para o toast; ver utils/mensagemDeErro.ts. */
  const [erroToast, setErroToast] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormState>(EMPTY_FORM);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  /* O toque no card abre os detalhes; editar é o ✎. */
  const [detalhe, setDetalhe] = useState<Reminder | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getReminders(activeMonth.key);
      setReminders(data);
    } catch (err) {
      console.error('Erro ao carregar lembretes:', err);
    } finally {
      setLoading(false);
    }
  }, [activeMonth.key]);

  useEffect(() => {
    load();
  }, [load, refreshTrigger]);

  const formatCurrency = (value: number) => brlOrMasked(value, !!hideValues);

  const openEdit = (r: Reminder) => {
    setEditingId(r.id);
    setFormData({
      description: r.description,
      type: r.type,
      value: r.value !== null ? String(r.value) : '',
      dayOfMonth: String(r.dayOfMonth),
      endDate: r.endDate ?? '',
      transactionId: r.transactionId ?? '',
      paymentMethodId: r.paymentMethodId ?? '',
    });
    setFormError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData(EMPTY_FORM);
    setFormError('');
  };

  /*
   * O voltar do celular fecha ESTE formulário antes de perguntar se quer sair.
   *
   * O relato foi exato: "quando estou com um formulário pra preencher, com o
   * teclado em evidência, e clico em voltar, queria que o form fechasse".
   * Só os modais e os menus estavam na pilha — os formulários que vivem DENTRO
   * da seção não, então voltar deles caía direto na pergunta de sair.
   */
  useFechavelComVoltar(showForm, closeForm);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.description.trim()) {
      setFormError('A descrição é obrigatória.');
      return;
    }
    const day = parseInt(formData.dayOfMonth);
    if (isNaN(day) || day < 1 || day > 31) {
      setFormError('Dia inválido (1–31).');
      return;
    }
    const value =
      formData.value !== '' ? parseFloat(formData.value) : null;
    if (formData.value !== '' && (isNaN(value!) || value! < 0)) {
      setFormError('Valor inválido.');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await updateReminder(editingId, {
          description: formData.description.trim(),
          type: formData.type,
          value,
          dayOfMonth: day,
          // `null` e não `''`: o servidor distingue "não mexeu" (undefined) de
          // "limpou" (null), e sem isso seria impossível TIRAR um prazo já
          // colocado.
          endDate: formData.endDate || null,
          transactionId: formData.transactionId || null,
          paymentMethodId: formData.paymentMethodId || null,
        });
      } else {
        await createReminder({
          description: formData.description.trim(),
          type: formData.type,
          value,
          dayOfMonth: day,
          endDate: formData.endDate || null,
          transactionId: formData.transactionId || null,
          paymentMethodId: formData.paymentMethodId || null,
        });
      }
      await load();
      onMudou?.();
      closeForm();
    } catch (e) {
      setFormError(mensagemDeErro(e, 'Não consegui salvar este lembrete.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r: Reminder) => {
    if (!await confirmar({
      titulo: 'Excluir este lembrete?',
      alvo: r.description,
      texto: 'O lançamento ligado a ele continua existindo, só para de ser cobrado por aqui.',
      acao: 'Excluir', perigo: true,
    })) return;
    try {
      await deleteReminder(r.id);
      await load();
      onMudou?.();
    } catch (e) {
      setErroToast(mensagemDeErro(e, 'Não consegui excluir o lembrete.'));
    }
  };

  const handleToggle = async (r: Reminder) => {
    try {
      if (r.checked) {
        await uncheckReminder(r.id, activeMonth.key);
      } else {
        await checkReminder(r.id, activeMonth.key);
      }
      setReminders(prev =>
        prev.map(item => item.id === r.id ? { ...item, checked: !item.checked } : item)
      );
      // Só DEPOIS de o servidor aceitar. Avisar antes deixaria a lista de
      // Transações mostrar ✓ para algo que não foi gravado.
      onMudou?.();
    } catch {
      console.error('Erro ao atualizar lembrete.');
    }
  };

  /*
   * Editar a partir do detalhe. O formulário nasce no alto da seção, acima da
   * lista: sem levar a vista até ele, o toque parecia não fazer nada. Só
   * `nearest`, e depois que o modal devolveu a rolagem — nada de jogar a
   * página para fora de Lembretes.
   */
  const formRef = React.useRef<HTMLFormElement>(null);
  const editarDoDetalhe = (r: Reminder) => {
    setDetalhe(null);
    openEdit(r);
    setTimeout(() => formRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }), 60);
  };

  const dataBR = (iso: string) => String(iso).slice(0, 10).split('-').reverse().join('/');
  const transacaoLigada = (r: Reminder) =>
    r.transactionId ? transactions.find(t => t.id === r.transactionId) : undefined;
  const cartaoDaFatura = (r: Reminder) =>
    r.paymentMethodId ? paymentMethods.find(m => m.id === r.paymentMethodId) : undefined;

  const done  = reminders.filter(r => r.checked).length;
  const total = reminders.length;
  const pct   = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rem-container">
      <Toast message={erroToast} onDismiss={() => setErroToast(null)} />
      <div className="rem-header">
        <div className="rem-title-group">
          <h2>Lembretes</h2>
          <span className="rem-month-badge">{activeMonth.label}</span>
        </div>

      </div>

      {total > 0 && (
        <div className="rem-progress-wrap">
          <div className="rem-progress-info">
            <span className="rem-progress-text">
              {done === total ? '✓ Todos concluídos' : `${done} de ${total} concluídos`}
            </span>
            <span className="rem-progress-pct">{pct}%</span>
          </div>
          <div className="rem-progress-bg">
            <div
              className={`rem-progress-fill ${done === total ? 'all-done' : ''}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      )}

      {/* Campo único — o ÚNICO caminho para criar. O formulário abaixo só
          aparece ao editar um lembrete existente. */}
      {!editingId && (
        <EntityQuickEntry
          modo="reminder"
          onCreate={async (d) => {
            await createReminder(d);
            await load();
          }}
        />
      )}

      {/* Form — só para EDITAR. Criar é sempre pelo campo único. */}
      {showForm && editingId && (
        <form className="rem-form" onSubmit={handleSave} ref={formRef}>
          <h3>Editar lembrete</h3>
          <div className="rem-form-grid">
            <div className="rem-form-group rem-col-span-2">
              <label>Descrição *</label>
              <input
                type="text"
                maxLength={LIMITES.descricao}
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                placeholder="Ex: Pagar fatura Nubank, Receber salário..."
                autoFocus
              />
            </div>
            <div className="rem-form-group">
              <label>Tipo *</label>
              <select
                value={formData.type}
                onChange={e => setFormData(p => ({ ...p, type: e.target.value as 'expense' | 'income' }))}
              >
                <option value="expense">💸 Gasto</option>
                <option value="income">💰 Recebimento</option>
              </select>
            </div>
            <div className="rem-form-group">
              <label>Dia do mês *</label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.dayOfMonth}
                onChange={e => setFormData(p => ({ ...p, dayOfMonth: e.target.value }))}
                placeholder="Ex: 10"
              />
            </div>
            <div className="rem-form-group">
              <label>Valor em R$ (opcional)</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.value}
                onChange={e => setFormData(p => ({ ...p, value: e.target.value }))}
                placeholder="0,00"
              />
            </div>
            <div className="rem-form-group">
              <label>Até quando (opcional)</label>
              <input
                type="date"
                onClick={abrirSeletorDeData}
                value={formData.endDate}
                onChange={e => setFormData(p => ({ ...p, endDate: e.target.value }))}
              />
              {/* O mês final conta INTEIRO, igual a uma despesa mensal com
                  data de fim. Dizer isso evita a leitura "termina no dia 10,
                  então novembro não aparece". */}
              <small className="rem-form-hint">
                {formData.endDate
                  ? 'O lembrete some depois deste mês. O mês da data ainda aparece inteiro.'
                  : 'Em branco, o lembrete vale para sempre.'}
              </small>
            </div>
            {cartoesComFatura.length > 0 && (
              <div className="rem-form-group rem-form-group-wide">
                <label>Fatura de qual cartão? (opcional)</label>
                <select
                  name="paymentMethodId"
                  value={formData.paymentMethodId}
                  onChange={e => setFormData(p => ({ ...p, paymentMethodId: e.target.value }))}
                >
                  <option value="">Não é lembrete de fatura</option>
                  {cartoesComFatura.map(m => (
                    <option key={m.id} value={m.id}>{m.name} · paga dia {m.billingDay}</option>
                  ))}
                </select>
                {/* 15/09: o mesmo fato em dois lugares — pagar um marca o outro. */}
                <small className="rem-form-hint">
                  Marcar este lembrete dá a fatura do mês como paga, e marcar a fatura como
                  paga marca este lembrete. No dia do pagamento, o aviso é este lembrete.
                </small>
              </div>
            )}
            <div className="rem-form-group rem-form-group-wide">
              <label>Ligar a um lançamento (opcional)</label>
              <select
                value={formData.transactionId}
                onChange={e => setFormData(p => ({ ...p, transactionId: e.target.value }))}
              >
                <option value="">Nenhum</option>
                {transacoesLigaveis.map(t => (
                  <option key={t.id} value={t.id}>
                    {t.type === TransactionType.INCOME ? '↑' : '↓'} {t.description}
                    {' · '}{fmtBRL(t.value)}
                    {' · '}{String(t.date).slice(0, 10).split('-').reverse().join('/')}
                    {t.isMonthly ? ' · mensal' : ''}
                  </option>
                ))}
              </select>
              {/* A ligação é o que faz a linha da lista mostrar ⏳ ou ✓. Marcar
                  este lembrete como feito confirma o lançamento naquele mês. */}
              <small className="rem-form-hint">
                O lançamento passa a mostrar “aguardando” até você marcar este
                lembrete como feito no mês. Numa despesa, o valor volta para o
                saldo enquanto não é paga; numa receita, ele sai do saldo
                enquanto não cai.
              </small>
            </div>
          </div>
          {formError && <p className="rem-form-error">{formError}</p>}
          <div className="rem-form-actions">
            <button type="button" className="rem-btn-cancel" onClick={closeForm}>Cancelar</button>
            <button type="submit" className="rem-btn-save" disabled={saving} aria-busy={saving}>
              {editingId ? 'Atualizar' : 'Adicionar'}
            </button>
          </div>
        </form>
      )}

      {/* O carregando só antes da primeira resposta: revalidar não pode
          trocar a lista por "Carregando" (a lição da página que pulava). */}
      {loading && reminders.length === 0 ? (
        <div className="loading-wrap">
          <div className="loading-ring" />
          <span className="loading-text">Carregando lembretes...</span>
        </div>
      ) : total === 0 ? (
        <div className="empty-preview-wrap">
          <span className="empty-preview-badge">Exemplo</span>
          <div className="rem-list empty-preview-ghost">
            {[
              { day: 5,  desc: 'Salário',          type: 'income'  as const, value: 4500.00, checked: true  },
              { day: 10, desc: 'Fatura Nubank',     type: 'expense' as const, value:  850.00, checked: false },
              { day: 20, desc: 'Aluguel',           type: 'expense' as const, value: 1200.00, checked: false },
            ].map((r, i) => (
              <div key={i} className={`rem-item ${r.checked ? 'rem-item-done' : ''} ${r.type}`}>
                <label className="rem-check-label">
                  <input type="checkbox" defaultChecked={r.checked} />
                  <span className="rem-check-box" />
                </label>
                <div className="rem-item-day">
                  <span className="rem-day-num">{r.day}</span>
                  <span className="rem-day-label">dia</span>
                </div>
                <div className="rem-item-body">
                  <span className={`rem-item-desc${r.checked ? ' rem-strikethrough' : ''}`}>{r.desc}</span>
                  <span className={`rem-type-badge ${r.type}`}>{r.type === 'income' ? 'Recebimento' : 'Gasto'}</span>
                </div>
                <div className={`rem-item-value ${r.type}${r.checked ? ' rem-muted' : ''}`}>
                  {formatCurrency(r.value)}
                </div>
                <div className="rem-item-actions">
                  <button className="rem-btn-delete">✕</button>
                </div>
              </div>
            ))}
          </div>
          <div className="empty-cta">
            <p className="empty-cta-title">Nenhum lembrete cadastrado</p>
            <p className="empty-cta-sub">Adicione lembretes de contas e recebimentos recorrentes para não perder nenhuma data.</p>
          </div>
        </div>
      ) : (
        <div className="rem-list">
          {reminders.map(r => (
            <div
              key={r.id}
              className={`rem-item ${r.checked ? 'rem-item-done' : ''} ${r.type}`}
              onClick={() => setDetalhe(r)}
            >
              <label className="rem-check-label" onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={r.checked}
                  onChange={() => handleToggle(r)}
                />
                <span className="rem-check-box" />
              </label>

              <div className="rem-item-day">
                <span className="rem-day-num">{r.dayOfMonth}</span>
                <span className="rem-day-label">dia</span>
              </div>

              <div className="rem-item-body">
                <span className={`rem-item-desc ${r.checked ? 'rem-strikethrough' : ''}`}>
                  {r.description}
                </span>
                <span className={`rem-type-badge ${r.type}`}>
                  {r.type === 'income' ? 'Recebimento' : 'Gasto'}
                </span>
              </div>

              {r.value !== null && (
                <div className={`rem-item-value ${r.type} ${r.checked ? 'rem-muted' : ''}`}>
                  {formatCurrency(r.value)}
                </div>
              )}

              <div className="rem-item-actions">
                <button
                  className="rem-btn-edit"
                  onClick={e => { e.stopPropagation(); openEdit(r); }}
                  title="Editar"
                  aria-label={`Editar ${r.description}`}
                >✎</button>
                <button
                  className="rem-btn-delete"
                  onClick={e => { e.stopPropagation(); handleDelete(r); }}
                  title="Excluir"
                >✕</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {detalhe && (() => {
        // A versão da lista, para o check refletir o toque feito no modal.
        const r = reminders.find(x => x.id === detalhe.id) ?? detalhe;
        const ligada = transacaoLigada(r);
        const cartao = cartaoDaFatura(r);
        return (
          <EdicaoModal titulo="Lembrete" centralNoDesktop onFechar={() => setDetalhe(null)}>
            <div className={`rem-detalhe ${r.type}`}>
              <div className="rem-detalhe-topo">
                <div className="rem-detalhe-dia">
                  <span className="rem-detalhe-dia-num">{r.dayOfMonth}</span>
                  <span className="rem-detalhe-dia-label">dia</span>
                </div>
                <div className="rem-detalhe-cabeca">
                  <h3 className="rem-detalhe-titulo">{r.description}</h3>
                  <span className="rem-detalhe-sub">
                    {r.type === 'income' ? 'Recebimento' : 'Gasto'}
                    {' · '}
                    <span className={`rem-detalhe-status ${r.checked ? 'feito' : 'pendente'}`}>
                      {r.checked ? '✓ feito' : 'pendente'} em {activeMonth.label.split(' ')[0].toLowerCase()}
                    </span>
                  </span>
                </div>
                {r.value !== null && (
                  <span className="rem-detalhe-valor">{formatCurrency(r.value)}</span>
                )}
              </div>

              <dl className="rem-detalhe-lista">
                <div>
                  <dt>Até quando</dt>
                  <dd>{r.endDate ? dataBR(r.endDate) : 'Sem prazo'}</dd>
                </div>
                {cartao && (
                  <div>
                    <dt>Fatura</dt>
                    <dd>{cartao.name}{cartao.billingDay ? ` · dia ${cartao.billingDay}` : ''}</dd>
                  </div>
                )}
                {r.transactionId && (
                  <div>
                    <dt>Lançamento</dt>
                    <dd>
                      {ligada ? (<>
                        {ligada.description}
                        <small>{fmtBRL(ligada.value)} · {dataBR(String(ligada.date))}</small>
                      </>) : 'De outro mês'}
                    </dd>
                  </div>
                )}
              </dl>

              <div className="rem-detalhe-acoes">
                <button
                  type="button" className="rem-detalhe-btn secundario"
                  onClick={() => editarDoDetalhe(r)}
                >
                  ✎ Editar
                </button>
                <button
                  type="button"
                  className={`rem-detalhe-btn ${r.checked ? 'secundario' : 'principal'}`}
                  onClick={() => handleToggle(r)}
                >
                  {r.checked ? 'Desmarcar' : 'Marcar como feito'}
                </button>
              </div>
            </div>
          </EdicaoModal>
        );
      })()}
    </div>
  );
};

export default Reminders;
